import { useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../../components/Button";
import Input from "../../components/Input";
import TermsAndConditions from "./components/TermsAndConditions";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { registerSchema } from "./schemas/authenticationSchema";

import { registerUser } from "./api/authRequest";

const SignUp = () => {
    const navigate = useNavigate();
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
    const [error, setError] = useState("");

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        watch,
    } = useForm({
        resolver: yupResolver(registerSchema),
    });

    const onSubmit = async (data) => {
        setError("");

        if (!termsAgreed) {
            setError("Please agree to the terms and conditions");
            return;
        }

        try {
            console.log(data);
            const result = await registerUser({
                ...data,
                city: "not required",
                state: "not required",
            });
            if (result?.status === 200) {
                localStorage.setItem("userId", result.data.userId);
                navigate("/questions");
            }
        } catch (err) {
            if (err.response?.status === 409) {
                setError("Account associated with this email already exists.");
            } else {
                setError(
                    err?.response?.data?.message ||
                        "Registration failed. Please try again."
                );
            }
        }
    };

    return (
        <>
            <div
                className="custom-container mx-auto w-4/5 lg:w-1/3"
                style={{ display: showTermsAndConditions ? "none" : "block" }}
            >
                <div className="text-center mx-auto mb-6">
                    <h4 className="text-lg md:text-2xl font-semibold mb-2">
                        Create an account
                    </h4>
                    <p>
                        The IISS provides a quick way to understand the severity
                        of a traumatic brain injury (TBI). A higher score
                        indicates a more severe injury. This score helps
                        patients and clinicians track the patient's recovery
                        progress and make informed decisions about treatment.
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-4"
                >
                    <Input
                        label="First Name"
                        name="firstName"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label="Last Name"
                        name="lastName"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label="Email"
                        type="email"
                        name="email"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label="Date of Birth"
                        type="date"
                        name="dob"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label="User Type"
                        name="userType"
                        type="select"
                        register={register}
                        errors={errors}
                        options={[
                            {
                                value: "emergencyMedTech",
                                label: "Emergency Med Tech",
                            },
                            { value: "firefighter", label: "Firefighter" },
                            { value: "policeOfficer", label: "Police Officer" },
                            { value: "other", label: "Other" },
                        ]}
                    />
                    <Input
                        label="Gender"
                        name="gender"
                        type="select"
                        register={register}
                        errors={errors}
                        options={[
                            {
                                value: "male",
                                label: "Male",
                            },
                            { value: "female", label: "Female" },
                            { value: "other", label: "Other" },
                        ]}
                    />
                    <Input
                        label="Postal Code"
                        name="postalCode"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label="Password"
                        type="password"
                        name="password"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label="Confirm Password"
                        type="password"
                        name="confirmPassword"
                        register={register}
                        errors={errors}
                    />

                    {error && <p className="text-red-600">{error}</p>}

                    <p>
                        Please accept the{" "}
                        <span
                            className="text-teal-600 text-semibold underline cursor-pointer"
                            onClick={() => setShowTermsAndConditions(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    setShowTermsAndConditions(true);
                                }
                            }}
                        >
                            terms and agreements
                        </span>
                        .
                    </p>

                    <Button type="submit" isLoading={isSubmitting}>
                        Register
                    </Button>
                </form>
                <hr className="mt-6 border border-gray-200 w-full p-0 mx-auto" />
                <div className="mt-4 px-2 flex justify-between items-center">
                    <p>Already have an account?</p>
                    <h6
                        className="text-teal-600 cursor-pointer"
                        onClick={() => navigate("/")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                navigate("/");
                            }
                        }}
                    >
                        Login
                    </h6>
                </div>
            </div>

            {showTermsAndConditions && (
                <TermsAndConditions
                    firstName={watch("firstName")}
                    lastName={watch("lastName")}
                    termsAgreed={termsAgreed}
                    setTermsAgreed={setTermsAgreed}
                    setShowTermsAndConditions={setShowTermsAndConditions}
                    setError={setError}
                />
            )}
        </>
    );
};

export default SignUp;
